// src/components/Financial/InvoiceBoletosTable.js

import React, { useEffect, useState, useCallback } from 'react'
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
    Chip,
    Link,
    Stack,
} from '@mui/material'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { api } from '../../../../../services/Main/Api'
import {Pix, Subtitles} from '@mui/icons-material'

const formatCurrency = (value) => {
    if (value == null) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    }).format(Number(value))
}

const formatDate = (value) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

const getStatusColor = (status) => {
    switch (status) {
        case 'Criado':
        case 'created':
        case 'CREATED':
            return 'info'

        case 'Pode ser registrado':
        case 'can_be_registered':
        case 'CAN_BE_REGISTERED':
            return 'primary'

        case 'Sendo registrado':
        case 'registering':
        case 'REGISTERING':
            return 'warning'

        case 'Registrado':
        case 'registered':
        case 'REGISTERED':
            return 'info'

        case 'Rejeitado':
        case 'rejected':
        case 'REJECTED':
            return 'error'

        case 'Pago':
        case 'paid':
        case 'PAID':
        case 'SETTLED':
        case 'settled':
            return 'success'

        case 'Cancelado':
        case 'canceled':
        case 'CANCELED':
            return 'error'

        case 'Erro interno':
        case 'INTERNAL_ERROR':
        case 'internal_error':
            return 'error'

        case 'Erro de saldo':
        case 'BALANCE_ERROR':
        case 'balance_error':
            return 'error'

        default:
            return 'default'
    }
}

/**
 * Componente de tabela de boletos de um Invoice.
 *
 * Props:
 *  - invoiceId (number | string): ID do invoice que terá os boletos listados
 */
const InvoicePixTable = ({ invoiceId, invoice }) => {
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchPixs = useCallback(async () => {
        if (!invoiceId) return

        setLoading(true)
        setError(null)

        try {
            const { data } = await api.get(`invoice/pix/`, {
                params: { invoice_id: invoiceId },
            })
            setResults(Array.isArray(data) ? data : data.results || [])
        } catch (err) {
            console.error('Erro ao carregar Pix do invoice', err)
            setError('Não foi possível carregar os Pix. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }, [invoiceId])

    useEffect(() => {
        fetchPixs()
    }, [fetchPixs])


    const handleCancelBoleto = async (boletoId) => {
        try {
            await api.patch(`invoice/pix/${boletoId}/`)
            fetchPixs() // atualizar tabela
        } catch (err) {
            console.error("Erro ao cancelar Pix:", err)
        }
    }


    if (!invoiceId) {
        return (
            <Card>
                <CardHeader title="Pix da fatura" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma fatura selecionado. Informe um <strong>invoiceId</strong> para listar os boletos.
                    </Typography>
                </CardContent>
            </Card>
        )
    }



    return (
        <Card sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <CardHeader
                avatar={<Pix sx={{ fontSize: 32 }} />}
                title="Pix"
            />
            <CardContent>
                {loading && (
                    <Box
                        sx={{
                            py: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!loading && error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && results.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum Pix foi gerado para esta fatura.
                    </Typography>
                )}

                {!loading && !error && results.length > 0 && (

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nº do Documento</TableCell>
                                <TableCell>Vencimento</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.map((boleto) => (
                                <TableRow key={boleto.id}>
                                    <TableCell>PIX-{boleto.id}</TableCell>
                                    <TableCell>{boleto.identifier || '-'}</TableCell>
                                    <TableCell>{formatDate(boleto.due_date)}</TableCell>
                                    <TableCell>{formatCurrency(boleto.value / 100)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={boleto.status || '—'}
                                            size="small"
                                            color={getStatusColor(boleto.status)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {/* Abrir PDF */}


                                            {/* Cancelar boleto — se não estiver pago ou cancelado */}
                                            {(!['Pago', 'Cancelado'].includes(boleto.status) && invoice?.status?.key === "paid") && (
                                                <Tooltip title="Cancelar Pix">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCancelBoleto(boleto.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <HighlightOffIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    )
}

export default InvoicePixTable
